import { ApiError } from "@/api/api";
import { Alert, Snackbar } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ApiErrorAlert } from "./ApiErrorAlert";

function useAutoClosingSnackbar(data: unknown, extraOnClose?: () => void) {
  const [open, setOpen] = useState(false);

  const onClose = useCallback(() => {
    setOpen(false);
    extraOnClose &&
      setTimeout(() => {
        extraOnClose();
      }, 200); // delay to make sure extraOnClose runs after alert disappearing animation finishes
  }, [setOpen, extraOnClose]);

  useEffect(() => {
    if (data) {
      setOpen(true);
    } else {
      onClose();
    }
  }, [data, onClose]);

  return {
    anchorOrigin: { vertical: "top", horizontal: "center" } as const,
    autoHideDuration: 6000,
    open,
    onClose,
  };
}

type AlertSnackbarProps = {
  children?: React.ReactNode;
  severity: "success" | "error" | "info" | "warning";
  onClose?: () => void;
};

export function AlertSnackbar({
  children,
  onClose,
  severity,
}: AlertSnackbarProps) {
  const props = useAutoClosingSnackbar(children, onClose);
  return (
    <Snackbar {...props}>
      <Alert onClose={props.onClose} severity={severity}>
        {children}
      </Alert>
    </Snackbar>
  );
}

export function ApiErrorSnackbar({
  error,
  onClose,
}: {
  error: ApiError | null;
  onClose?: () => void;
}) {
  const props = useAutoClosingSnackbar(error, onClose);
  return (
    <Snackbar {...props}>
      <ApiErrorAlert onClose={props.onClose} error={error} />
    </Snackbar>
  );
}
